import { useTranslation } from "react-i18next";
import { timezones, locales } from "@dashboard-v3/shared";
import { Box, MenuItem, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { grey } from "@material-ui/core/colors";
import SelectField from "components/Forms/SelectField";
import { Label } from "components/Styled";
import { useRuleContext } from "./context/ruleContext";

type Timezone = (typeof timezones)[number];
type TimeZoneValue = { label: string; value: string };

export default function LocaleTimezone() {
  const { t } = useTranslation("orgAndDomains");
  const { state, dispatch } = useRuleContext();
  const { locale, timeZone } = state.rule;
  const tzValue = timeZone
    ? timezones.find(tz => tz.value === timeZone)
    : null;

  const onLocaleChange = event => {
    const { value } = event.target;

    dispatch({
      type: "updateRuleLabels",
      payload: { type: "locale", input: value },
    });
  };

  const onTimeZoneChange = (_, option: string | TimeZoneValue) => {
    let value = null;

    if (typeof option !== "string") {
      value = option?.value ?? null;
    }

    dispatch({
      type: "updateRuleLabels",
      payload: { type: "timeZone", input: value },
    });
  };

  const getTimeZoneOptionLabel = (option: TimeZoneValue) => {
    if (option && option.label) {
      return option.label;
    }
  };

  return (
    <Box data-testid="LocaleTimezone">
      <Label wording={t("org.locale&timezone")} />
      <Box my={2} display="flex" style={{ gap: "15px" }}>
        <SelectField
          name="locale"
          label={t("org.locale")}
          fullWidth
          displayEmpty
          options={["", ...locales]}
          value={locale ?? ""}
          onChange={onLocaleChange}
          renderOption={locale => (
            <MenuItem key={locale || "empty-locale"} value={locale || null}>
              {locale && <>{t(`org.${locale}`)}</>}
              {!locale && (
                <i style={{ color: grey[400] }}>{t("org.organizationDefault")}</i>
              )}
            </MenuItem>
          )}
        />
        <Autocomplete
          fullWidth
          getOptionLabel={getTimeZoneOptionLabel}
          getOptionSelected={compare}
          options={timezones}
          value={tzValue}
          onChange={onTimeZoneChange}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label={t("org.timezone")}
            />
          )}
        />
      </Box>
    </Box>
  );
}

function compare(one: Timezone, other: Timezone) {
  if (one && other) {
    return one.value === other.value;
  }
}
